import Color from 'color';
import { useEffect, useMemo, useRef, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import type { Theme } from '@hivebrite/design-system/Theme';
import { useOnClickOutside } from '@hivebrite/react-hooks';

import { getColorPresetsArray, getNetworkColor } from 'hb-builder/utils/colors';
import ColorPicker from 'hb-react/backoffice/components/DesignSystem/ColorPicker';
import { addToStorage, getItem } from 'hb-react/shared/utils/sessionStorage';

const COLOR_PRESETS_LIMIT = 3;
const SESSION_STORAGE_KEY = 'hivebrite-new-builder-colors';

const initialColorPresets = getItem({ key: SESSION_STORAGE_KEY });

interface ColorInputProps {
  id: string;
  onChange: (args: { value: string }) => void;
  resetAllColorPickers: () => void;
  externalControl: {
    enable: boolean;
    isPickerOpen: (args: { id: string }) => boolean;
    togglePicker: (args: { forceClose?: boolean; id: string }) => void;
  };
  brandColors?: Record<'label' | 'value', string>[];
  componentHasMultiplePickers?: boolean;
  hasTransparent?: boolean;
  hasOpacity?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  style?: Record<string, any>;
  value?: { color: string; opacity: number | string } | string;
  theme: Theme;
}

const ColorInput = ({
  brandColors,
  componentHasMultiplePickers,
  externalControl: {
    enable: enableExternalControl,
    isPickerOpen,
    togglePicker,
  },
  id,
  hasTransparent,
  hasOpacity,
  onBlur,
  onChange,
  onFocus,
  resetAllColorPickers,
  style,
  value: initialValue,
  theme,
}: ColorInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [pickerOpen, setPickerOpen] = useState(
    enableExternalControl ? isPickerOpen?.({ id }) : false,
  );

  const value = useMemo(
    () => getNetworkColor(initialValue, theme?.colors),
    [initialValue, theme?.colors],
  );

  const [colorPresets, setColorPresets] = useState(
    getColorPresetsArray({
      colors: initialColorPresets,
      theme,
      length: 3,
    }),
  );

  const handleForceClose = () => {
    if (isPickerOpen?.({ id })) {
      return componentHasMultiplePickers
        ? resetAllColorPickers()
        : togglePicker({ forceClose: true, id });
    }
  };

  useOnClickOutside(wrapperRef, handleForceClose);

  useEffect(() => {
    if (value && !pickerOpen) {
      // Do not add brand colors to the presets as they are already there.

      if (
        brandColors &&
        brandColors?.map(({ value }) => value).indexOf(value.toLowerCase()) ===
          -1
      ) {
        addToStorage({
          allowDuplicate: false,
          key: SESSION_STORAGE_KEY,
          value,
          limit: COLOR_PRESETS_LIMIT,
        });
      }
    }

    if (pickerOpen) {
      const storedColors = getItem({ key: SESSION_STORAGE_KEY });

      setColorPresets(
        getColorPresetsArray({ colors: storedColors, theme, length: 3 }),
      );
    }
  }, [value, brandColors, pickerOpen, theme]);

  const listColors = useMemo(
    () =>
      [
        brandColors && {
          name: 'brandColors',
          colors: brandColors.map(({ label, value }) => ({
            label,
            value,
            withTooltip: true,
          })),
        },
        (hasTransparent || colorPresets) && {
          name: 'presets',
          colors: [
            hasTransparent && { value: 'transparent' },
            ...colorPresets
              .map((color, index) => ({
                label: `label-${index}`,
                value: Color(color).hex().toLowerCase(),
              }))
              // When transparent color is enabled, remove one stored preset colors
              .splice(hasTransparent ? 1 : 0, colorPresets.length),
          ].filter(Boolean),
        },
      ].filter(Boolean),
    [brandColors, colorPresets, hasTransparent],
  );

  const transitionsPicker = useTransition(pickerOpen, {
    from: {
      opacity: 0,
      pointerEvents: 'none',
      y: theme.space[3],
    },
    enter: {
      opacity: 1,
      pointerEvents: 'all',
      y: theme.space[2],
    },
    leave: {
      opacity: 0,
      pointerEvents: 'none',
      y: theme.space[3],
    },
  });

  const handleTogglePicker = () =>
    enableExternalControl
      ? togglePicker({ id })
      : setPickerOpen((prevState) => !prevState);

  const handleChangeColor = (newValue) => {
    if (value !== newValue) {
      onChange({ value: newValue });
      // Close the picker if transparent color
      if (newValue === undefined) {
        handleTogglePicker();
      }
    }
  };

  const initialAlpha = hasTransparent && value ? Color(value).alpha() : 1;

  useEffect(() => {
    if (enableExternalControl) {
      setPickerOpen(isPickerOpen?.({ id }));
    }
  }, [enableExternalControl, id, isPickerOpen]);

  return (
    <Wrapper ref={wrapperRef}>
      <CurrentColorWrapper onClick={handleTogglePicker}>
        <CurrentColor
          value={
            !value
              ? 'transparent'
              : Color(value).alpha(initialAlpha).rgb().string()
          }
        />
      </CurrentColorWrapper>

      {transitionsPicker(
        (styles, item) =>
          item && (
            <PickerWrapper
              style={{
                ...style,
                opacity: styles.opacity,
                pointerEvents: styles.pointerEvents,
                transform: styles.y.to((y) => `translate3d(0, ${y}px, 0)`),
              }}
            >
              <ColorPicker
                customOpacity={hasOpacity}
                defaultColor={null}
                id={id}
                onBlur={onBlur}
                onChange={handleChangeColor}
                onFocus={onFocus}
                presetColors={listColors}
                value={value}
              />
            </PickerWrapper>
          ),
      )}
    </Wrapper>
  );
};

export default ColorInput;

const CurrentColor = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['value'].includes(prop),
  })
  .attrs(() => ({
    height: 24,
    width: 54,
  }))<{ value: string }>`
    ${({ value }) => `
    border-radius: 2px;
    position: relative;

    ${
      value === 'transparent'
        ? `
      overflow: hidden;

      ::before {
        position: absolute;
        content: '';
        background: red;
        display: block;
        width: 130%;
        height: 3px;
        transform: rotate(-25deg);
        left: -7px;
        right: -7px;
        top: -7px;
        bottom: -7px;
        margin: auto;
      }
      `
        : `background-color: ${value}`
    }
  `}
  `;

const CurrentColorWrapper = styled(Box).attrs(() => ({
  p: '3px',
}))`
  border: ${({ theme }) => `1px solid ${theme.colors.asphalt}`};
  border-radius: 3px;
  cursor: pointer;
`;

const PickerWrapper = styled(animated.div)<any>`
  bottom: 180px;
  position: fixed;
  right: 90px;
  z-index: 10;
`;

const Wrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  flex: 1,
  justifyContent: 'flex-end',
}))`
  position: relative;
`;
